// dependencies
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { AnyAction } from "redux";

import { faPlus } from "@fortawesome/pro-solid-svg-icons";

import { Pagination } from "@performyard/py-components";
import { ADMIN_ROLES } from "@performyard/utils-isomorphic";

//components
import DownloadButton from "../../../../../components.smart/DownloadButton/DownloadButton";
import Button from "../../../../../components/Button";
import ThumbSwitch from "../../../../../components/Inputs/ToggleSwitches/ThumbSwitch";
import PageHeading from "../../../../../components/PageHeading";
import { SideNavigationLink } from "../../../../../components/SideNavigation";
import Typography from "../../../../../components/Text";
import ArchiveGoalModal from "./components/ArchiveGoalsModal";
import CompleteGoalsModal from "./components/CompleteGoalsModal";
import GoalsListFilters, { GoalsFilters } from "./components/Filters";
import GoalsListBulkActionsBar from "./components/GoalsListBulkActionsBar";
import GoalsListTable from "./components/GoalsListTable";
import ReadableGoalsFilterChips from "./components/ReadableFiltersList";

//hooks
import {
	useCompanyFields,
	useCompanySettings,
	useCurrentUser,
	useInternalApiUser,
	useRbacPrimaryRolePermissions,
	useSearchService,
	useSearchServicePrefetch,
} from "../../../../../api/hooks";
import { useIsNewNavigation } from "../../../../../api/hooks/defs/navigation";
import { GOAL_TYPE, useBulkSelection } from "../../../../../hooks/useBulkSelection";

// services
import { SERVICE_GOAL, getSearch } from "../../../../../api/services/defs/search-service";
import { Goal } from "../../../../../api/services/searchServiceResultTypes/goal";

// selectors
import { StateChange } from "../../../../../actions/page.actions";

import { getCsvDownloadJobID } from "./selectors";

// utils
import pubSub from "../../../../../utils/PubSub";
import { convertFiltersForBulkSelectionQuery, getGoalsListSearchServiceFilters, getIsGroupFieldVisible } from "./utils";

// page template
import InfoPageTemplate from "../../../../../pageTemplates/InfoPageTemplate";
import StandardToolbarActionRow from "../../../../../pageTemplates/InfoPageTemplate/StandardToolbarActionRow";
import useInfoPageTemplateQueryState from "../../../../../pageTemplates/InfoPageTemplate/useInfoPageTemplateQueryState";
// constants
import { GOALS_SIDE_NAVIGATION_LINKS } from "../../constants";
import config from "./config";

type GoalsListProps = {
	userID: string;
	userCanExportToCsv?: boolean;
	userCanBulkEditGoals?: boolean;
	navItems: Array<SideNavigationLink>;
};

const PAGE_TITLE = "Company Goals";

const GoalsList = ({ userID, userCanBulkEditGoals = false, userCanExportToCsv = false, navItems }: GoalsListProps) => {
	const dispatch = useDispatch();
	const { isLoading: isCompanySettingsLoading } = useCompanySettings();
	const { data: permissions } = useRbacPrimaryRolePermissions("goal");
	const isNewNav = useIsNewNavigation();

	const { data: currentUser } = useCurrentUser();
	const userRole = currentUser?.me?.authorization?.payload?.user_primary_role;
	const userId = currentUser?.me?.authorization?.payload?.user_id ?? "";
	const companyId = currentUser?.me?.authorization?.payload?.company_id ?? "";
	const { data: companyFields } = useCompanyFields(companyId);
	const isAdmin = ADMIN_ROLES.includes(userRole ?? "");
	const { data: userInfo } = useInternalApiUser(userId, { enabled: !isAdmin && !!userId });

	const isGroupFieldVisible = useMemo(
		() => getIsGroupFieldVisible(companyFields, isAdmin, userInfo),
		[companyFields, isAdmin, userInfo]
	);

	const canUserCreateGoals = permissions?.includes("goals:createGoal");

	const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
	const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);
	const [isBulkSelectionActive, setIsBulkSelectionActive] = useState(false);
	const [isFiltersOpen, setIsFiltersOpen] = useState(false);

	const goalWizardBaseUrl = "/goal/wizard/";
	const goalWizardUrlParams = `assignee=${userID}`;

	const downloadCsvJobID = useSelector((state) => getCsvDownloadJobID(state));

	const {
		pageNumber,
		setPageNumber,
		sort,
		setSort,
		filters,
		setFilters,
		searchValue,
		debouncedSearchValue,
		setDebouncedSearch,
	} = useInfoPageTemplateQueryState<GoalsFilters>({
		defaultSortName: "end_date",
		defaultSortDirection: "desc",
		defaultPageNumber: 1,
		fallbackFilters: {
			status: ["active"],
			transparency: ["public"],
		},
	});

	const {
		data,
		isLoading,
		refetch: refetchGoals,
	} = useSearchService<Goal>({
		service: SERVICE_GOAL,
		filters: getGoalsListSearchServiceFilters(filters, debouncedSearchValue),
		skip: (pageNumber - 1) * config.pageSize,
		limit: config.pageSize,
		sort,
		defaultErrorMessage: "There was an error getting Goals",
		disabled: isCompanySettingsLoading,
	});

	//prefetch page before and 3 pages after
	useSearchServicePrefetch(
		{
			service: SERVICE_GOAL,
			filters: getGoalsListSearchServiceFilters(filters, debouncedSearchValue),
			skip: (pageNumber - 2) * config.pageSize,
			limit: config.pageSize,
			sort,
			disabled: isCompanySettingsLoading,
		},
		pageNumber <= 1 ? 0 : 1
	);

	useSearchServicePrefetch(
		{
			service: SERVICE_GOAL,
			filters: getGoalsListSearchServiceFilters(filters, debouncedSearchValue),
			skip: pageNumber * config.pageSize,
			limit: config.pageSize,
			sort,
			disabled: isCompanySettingsLoading,
		},
		3
	);

	const goals = data?.results || [];
	const totalGoalsCount = data?.total || 0;

	const {
		selections,
		add,
		remove,
		addQuery,
		clearAll,
		applyBulkUpdate,
		isQuerying,
		isInitialized,
		isApplyingBulkUpdate,
	} = useBulkSelection(GOAL_TYPE, {
		onBulkUpdateSuccess: () => refetchGoals(),
	});

	useEffect(() => {
		clearAll();
	}, [JSON.stringify(filters), debouncedSearchValue]);

	useEffect(() => {
		if (!isBulkSelectionActive && selections.length > 0) {
			clearAll();
		}
	}, [isBulkSelectionActive]);

	const handleDownloadCsv = async () => {
		const response = await getSearch(
			SERVICE_GOAL,
			getGoalsListSearchServiceFilters(filters, debouncedSearchValue),
			0,
			0,
			sort,
			true
		);

		const jobID = response?.jobID;
		pubSub.publish("check async frequency", { frequency: 500 });
		dispatch(StateChange(config.pageName, ["downloadCsvJobID"], jobID) as unknown as AnyAction);
	};

	const handleClearCsvDownload = () => {
		dispatch(StateChange(config.pageName, ["downloadCsvJobID"], null) as unknown as AnyAction);
	};

	const actionButtons = [
		userCanExportToCsv && (
			<DownloadButton
				autoDownload={true}
				jobID={downloadCsvJobID}
				isHollow={true}
				data-testid="downloadCSVButton"
				onClick={handleDownloadCsv}
				onDownload={handleClearCsvDownload}
			>
				Download CSV
			</DownloadButton>
		),
		canUserCreateGoals && (
			<Button
				key="create-goal"
				testid="create-goal-button"
				href={`${goalWizardBaseUrl}?${goalWizardUrlParams}`}
				icon={faPlus}
				color="green"
			>
				Create Goal
			</Button>
		),
	];

	const secondaryActionButtons = [
		userCanBulkEditGoals && (
			<div className="flex items-center gap-2" key="bulk-actions-toggle">
				<Typography size="paragraph-3" weight="regular">
					Bulk Actions:
				</Typography>
				<ThumbSwitch value={isBulkSelectionActive} onChange={(value) => setIsBulkSelectionActive(value)} />
			</div>
		),
	];

	const toolbarActionRow = (
		<StandardToolbarActionRow
			searchInput={searchValue}
			onSearchInputChange={setDebouncedSearch}
			onFilterButtonPress={() => setIsFiltersOpen(!isFiltersOpen)}
			activeFiltersCount={Object.keys(filters).length}
			searchPlaceholder="Search Goals..."
		/>
	);

	const toolbarInformationRow = (
		<ReadableGoalsFilterChips filters={filters} setFilters={(newFilters) => setFilters(newFilters)} />
	);

	const drawer = (
		<GoalsListFilters
			isOpen={isFiltersOpen}
			onClose={() => setIsFiltersOpen(false)}
			filters={filters}
			onApplyFilters={(newFilters) => setFilters(newFilters as any)}
			isGroupFieldVisible={isGroupFieldVisible}
		/>
	);

	const goalIDs = goals.map((goal) => goal._id);
	const allPageRowsSelected = goalIDs.length > 0 && goalIDs.every((goalID) => selections.includes(goalID));
	const shouldShowSelectAll = allPageRowsSelected && selections.length < totalGoalsCount;
	const shouldShowClearSelection = selections.length >= totalGoalsCount && totalGoalsCount > 0;

	// Handle "Select All" checkbox in bulk actions bar and table header
	const handleSelectAllPageChange = (isSelected: boolean) => {
		if (isSelected) {
			add(goalIDs);
		} else {
			remove(goalIDs);
		}
	};

	const mainContent = (
		<div>
			<div
				className={`overflow-hidden transition-all duration-300 ease-in-out ${
					isBulkSelectionActive ? "max-h-14" : "max-h-0"
				}`}
			>
				<GoalsListBulkActionsBar
					disabled={!userCanBulkEditGoals || !isBulkSelectionActive}
					selectedCount={selections.length}
					totalCount={totalGoalsCount}
					onCompleteClick={() => setIsCompleteModalOpen(true)}
					onArchiveClick={() => setIsArchiveModalOpen(true)}
					onSelectAll={() => {
						addQuery(convertFiltersForBulkSelectionQuery(filters, searchValue));
					}}
					onClearSelection={() => clearAll()}
					isQuerying={isQuerying}
					isInitialized={isInitialized}
					shouldShowSelectAll={shouldShowSelectAll}
					shouldShowClearSelection={shouldShowClearSelection}
					allPageRowsSelected={allPageRowsSelected}
					onSelectAllPageChange={handleSelectAllPageChange}
				/>
			</div>

			<div>
				<GoalsListTable
					goals={goals}
					isFetchingGoals={isLoading || isApplyingBulkUpdate || isCompanySettingsLoading}
					handleColumnHeaderClick={(sortValue) => setSort(sortValue)}
					activeSort={sort.name}
					activeSortDirection={sort.direction}
					isBulkSelectionEnabled={isBulkSelectionActive && userCanBulkEditGoals}
					selectedGoalIds={selections}
					onAddGoals={add}
					onRemoveGoals={remove}
					allPageRowsSelected={allPageRowsSelected}
					onSelectAllPageChange={handleSelectAllPageChange}
					isGroupFieldVisible={isGroupFieldVisible}
				/>
				{totalGoalsCount > 0 && (
					<div
						data-testid="paginationContainer"
						className="mx-auto mt-5 flex w-full max-w-[400px] justify-center"
					>
						<Pagination
							currentPage={pageNumber}
							pageCount={Math.ceil(totalGoalsCount / config.pageSize)}
							onPageChange={(newPage: number) => setPageNumber(newPage)}
						/>
					</div>
				)}
			</div>

			{isArchiveModalOpen && (
				<ArchiveGoalModal
					numberOfGoals={selections?.length}
					onClose={() => setIsArchiveModalOpen(false)}
					onUpdate={async () => {
						await applyBulkUpdate({ status: "archived" });
						refetchGoals();
					}}
				/>
			)}
			{isCompleteModalOpen && (
				<CompleteGoalsModal
					numberOfGoals={selections?.length}
					onClose={() => setIsCompleteModalOpen(false)}
					onUpdate={async (message) => {
						await applyBulkUpdate({ status: "done", message });
					}}
				/>
			)}
		</div>
	);

	return (
		<InfoPageTemplate
			actionButtons={actionButtons}
			secondaryActionButtons={secondaryActionButtons}
			toolbarActionRow={toolbarActionRow}
			toolbarInformationRow={toolbarInformationRow}
			drawer={drawer}
			mainContent={mainContent}
			pageTitle={PAGE_TITLE}
			pageHeading={isNewNav ? <PageHeading>{PAGE_TITLE}</PageHeading> : undefined}
			navigationLinks={navItems}
			activeNavigationLinkKey={GOALS_SIDE_NAVIGATION_LINKS[0].key}
		/>
	);
};

export default GoalsList;
